import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyApplications } from "../../api/jobs";
import api from "../../api/client";
import StatusBadge from "../../components/ui/ui/StatusBadge";
import { Button } from "../../components/ui/button";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { getJobPath } from "../../utils/jobSlug";
import { glassCard, glassCardHover, glassPanel } from "../../utils/glassStyles";
import {
  Loader2,
  MapPin,
  DollarSign,
  Calendar,
  Building2,
  Eye,
  Briefcase,
  Trash2,
  Filter,
} from "lucide-react";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "PENDING", label: "Pending" },
  { id: "ACCEPTED", label: "Accepted" },
  { id: "REJECTED", label: "Rejected" },
  { id: "other", label: "Other" },
];

const matchesStatusFilter = (status, filterId) => {
  const normalized = (status || "PENDING").toUpperCase();
  if (filterId === "all") return true;
  if (filterId === "other") return !["PENDING", "ACCEPTED", "REJECTED"].includes(normalized);
  return normalized === filterId;
};

const MyApplications = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const res = await getMyApplications();
        const validApps = (res.data || []).filter((app) => app && app.job);
        setApps(validApps);
      } catch (err) {
        console.error("Failed to fetch applications:", err);
        setError("Failed to load applications. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, []);

  const handleWithdraw = async (appId) => {
    setActionLoading((prev) => ({ ...prev, [appId]: true }));
    try {
      await api.delete(`/applier/applications/${appId}`);
      setApps((prev) => prev.filter((a) => a.id !== appId));
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to withdraw application.");
    } finally {
      setActionLoading((prev) => ({ ...prev, [appId]: false }));
    }
  };

  const filteredApps = apps.filter((app) => matchesStatusFilter(app.status, statusFilter));

  const getFilterCount = (filterId) =>
    apps.filter((app) => matchesStatusFilter(app.status, filterId)).length;

  if (loading) {
    return (
      <div className="min-h-screen relative bg-transparent flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  if (error && apps.length === 0) {
    return (
      <div className="min-h-screen relative bg-transparent flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-destructive text-base sm:text-lg mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} className="rounded-xl">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="applier-page">
      <div className="applier-page-inner">
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-primary to-accent shadow-lg mb-4">
            <Briefcase className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3">
            My{" "}
            <span className="text-brand-gradient">
              Applications
            </span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
            Track the status of your job applications and stay updated on your career journey.
          </p>
        </div>

        {error && (
          <p className="text-destructive text-sm text-center mb-4">{error}</p>
        )}

        {apps.length > 0 && (
          <div className={`rounded-2xl ${glassPanel} py-3 px-3 sm:py-4 sm:px-5 mb-6 sm:mb-8`}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-sm font-semibold flex items-center gap-2 shrink-0">
                <Filter className="w-4 h-4 text-primary" />
                Filter by status
              </p>
              <p className="text-xs text-muted-foreground shrink-0">
                {filteredApps.length} of {apps.length}
              </p>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none snap-x snap-mandatory">
              {STATUS_FILTERS.map((f) => (
                <Button
                  key={f.id}
                  size="sm"
                  variant={statusFilter === f.id ? "default" : "outline"}
                  onClick={() => setStatusFilter(f.id)}
                  className={`rounded-full shrink-0 snap-start h-9 px-3.5 text-xs sm:text-sm backdrop-blur-sm ${
                    statusFilter !== f.id
                      ? "bg-white/10 dark:bg-primary/20 border-white/20 dark:border-primary/20 hover:bg-white/20 dark:hover:bg-primary/30"
                      : "shadow-md shadow-primary/20"
                  }`}
                >
                  {f.label}
                  <span className="ml-1.5 opacity-80">({getFilterCount(f.id)})</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {apps.length === 0 ? (
          <div className={`text-center py-14 sm:py-16 rounded-2xl ${glassCard} max-w-md mx-auto`}>
            <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-base sm:text-lg font-semibold mb-2">No Applications Yet</p>
            <p className="text-sm text-muted-foreground mb-6 px-4">
              Start exploring and applying to jobs that match your skills and interests.
            </p>
            <Link to="/jobs">
              <Button className="rounded-xl">Browse Jobs</Button>
            </Link>
          </div>
        ) : filteredApps.length === 0 ? (
          <div className={`text-center py-14 sm:py-16 rounded-2xl ${glassCard} max-w-md mx-auto`}>
            <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-base sm:text-lg font-semibold mb-2">No {STATUS_FILTERS.find((f) => f.id === statusFilter)?.label.toLowerCase()} applications</p>
            <p className="text-sm text-muted-foreground mb-6 px-4">
              Try a different status filter to see your other applications.
            </p>
            <Button variant="outline" onClick={() => setStatusFilter("all")} className="rounded-xl">
              Show all applications
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-5">
            {filteredApps.map((app, index) => {
              const job = app.job || {};
              const jobTitle = job.title || "Job Title Not Available";
              const companyName =
                job.recruiter?.companyName || job.companyName || "Company Not Available";
              const location = job.location || "Location Not Specified";
              const salaryMin = job.salaryMin || 0;
              const salaryMax = job.salaryMax || 0;
              const hasSalary = salaryMin > 0 || salaryMax > 0;
              const jobId = job.id || app.jobId || `app-${app.id}`;

              return (
                <div
                  key={app.id || `app-${index}`}
                  className={`${glassCardHover} p-4 sm:p-5 rounded-2xl flex flex-col h-full`}
                >
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-foreground hover:text-primary transition-colors mb-2 line-clamp-2 leading-snug">
                        {jobTitle}
                      </h3>
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-xs sm:text-sm flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{companyName}</span>
                        </p>
                        <p className="text-muted-foreground text-xs sm:text-sm flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{location}</span>
                        </p>
                        {hasSalary && (
                          <p className="text-muted-foreground text-xs sm:text-sm flex items-center gap-1.5">
                            <DollarSign className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">
                              {salaryMin.toLocaleString()} - {salaryMax.toLocaleString()} ETB
                            </span>
                          </p>
                        )}
                        <p className="text-muted-foreground text-xs sm:text-sm flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 shrink-0" />
                          {new Date(app.appliedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>

                  {app.coverLetter && (
                    <div className="mb-3 p-3 bg-muted/30 rounded-xl border border-border/50">
                      <p className="text-xs sm:text-sm text-foreground/90 line-clamp-3">
                        <strong className="text-primary">Cover Letter:</strong> {app.coverLetter}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2 mt-auto pt-2">
                    <Link to={getJobPath({ id: jobId, title: jobTitle })} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full gap-1.5 rounded-xl">
                        <Eye className="w-4 h-4" />
                        View Job
                      </Button>
                    </Link>
                    {app.status === "PENDING" && (
                      <ConfirmDialog
                        title="Withdraw Application?"
                        description="This action cannot be undone. Your application will be permanently removed."
                        onConfirm={() => handleWithdraw(app.id)}
                        trigger={
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={actionLoading[app.id]}
                            className="flex-1 sm:flex-none rounded-xl gap-1.5"
                          >
                            {actionLoading[app.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Trash2 className="w-4 h-4" />
                                Withdraw
                              </>
                            )}
                          </Button>
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplications;
